import { GRID_BEATS, type GridValue } from "./music.ts";

export type MelodyTapRawGesture = {
  id: number;
  startTime: number;
  endTime: number;
};

export type MelodyTapGesture = MelodyTapRawGesture & {
  durationMs: number;
  startBeat: number;
  durationBeats: number;
  intervalFromPrevious: number | null;
  intervalFromPreviousBeats: number | null;
};

export type MelodyTapRecommendation = {
  grid: GridValue;
  minimumNoteBeats: number;
  noteDetail: number;
  lengthAssist: true;
};

export type MelodyTapAnalysis = {
  bpm: number;
  gestures: MelodyTapGesture[];
  onsetCount: number;
  shortestSignificantBeats: number;
  shortestSignificantMs: number;
  averageNoteLengthBeats: number;
  maximumHoldBeats: number;
  recommendedDivision: GridValue;
  densityLabel: "ゆったり" | "ほどよい" | "細かい" | "かなり細かい";
  repeatedOnsetIntent: boolean;
  recommendation: MelodyTapRecommendation;
};

const DIVISIONS: Array<{ grid: GridValue; beats: number }> = [
  { grid: "1/32", beats: GRID_BEATS["1/32"] },
  { grid: "1/16", beats: GRID_BEATS["1/16"] },
  { grid: "1/8", beats: GRID_BEATS["1/8"] },
  { grid: "1/4", beats: GRID_BEATS["1/4"] },
];

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function nearestDivision(beats: number): { grid: GridValue; beats: number } {
  const safe = Math.max(GRID_BEATS["1/32"], beats);
  return [...DIVISIONS].sort((a, b) => Math.abs(Math.log(safe / a.beats)) - Math.abs(Math.log(safe / b.beats)))[0];
}

function significantDivision(intervals: number[]): { grid: GridValue; beats: number } {
  if (!intervals.length) return DIVISIONS.at(-1) as { grid: GridValue; beats: number };
  const classified = intervals.map(nearestDivision);
  // A single very short interval is treated as a possible mistap. The finest
  // division must occur at least twice and cover a meaningful part of input.
  for (const division of DIVISIONS) {
    const count = classified.filter((item) => item.grid === division.grid).length;
    if (count >= 2 && count / classified.length >= 0.25) return division;
  }
  return nearestDivision(median(intervals));
}

function coefficientOfVariation(values: number[]): number {
  if (values.length < 2) return Number.POSITIVE_INFINITY;
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  if (!mean) return Number.POSITIVE_INFINITY;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance) / mean;
}

export function convertMelodyTapGestures(rawGestures: MelodyTapRawGesture[], bpm: number): MelodyTapGesture[] {
  if (!rawGestures.length) return [];
  const beatMs = 60_000 / bpm;
  const ordered = [...rawGestures].sort((a, b) => a.startTime - b.startTime);
  const origin = ordered[0].startTime;
  return ordered.map((gesture, index) => {
    const previous = ordered[index - 1];
    const interval = previous ? gesture.startTime - previous.startTime : null;
    const durationMs = Math.max(0, gesture.endTime - gesture.startTime);
    return {
      ...gesture,
      durationMs,
      startBeat: (gesture.startTime - origin) / beatMs,
      durationBeats: durationMs / beatMs,
      intervalFromPrevious: interval,
      intervalFromPreviousBeats: interval === null ? null : interval / beatMs,
    };
  });
}

export function analyzeMelodyTaps(rawGestures: MelodyTapRawGesture[], bpm: number): MelodyTapAnalysis {
  const gestures = convertMelodyTapGestures(rawGestures, bpm);
  const beatMs = 60_000 / bpm;
  const intervals = gestures
    .map((gesture) => gesture.intervalFromPreviousBeats)
    .filter((value): value is number => value !== null);
  // Ignore impossible double-fire intervals below 1/64 beat without using them
  // as evidence for a finer GRID.
  const usableIntervals = intervals.filter((value) => value >= 0.0625);
  const division = significantDivision(usableIntervals);
  const explicitHoldThresholdMs = Math.max(180, beatMs * 0.5);
  const intendedLengths = gestures.map((gesture, index) => {
    if (gesture.durationMs >= explicitHoldThresholdMs) return gesture.durationBeats;
    const nextInterval = gestures[index + 1]?.intervalFromPreviousBeats;
    return nextInterval ?? division.beats;
  });
  const holds = gestures.filter((gesture) => gesture.durationMs >= explicitHoldThresholdMs);
  const holdRatio = gestures.length ? holds.length / gestures.length : 0;
  const repeatedOnsetIntent = usableIntervals.length >= 2 && coefficientOfVariation(usableIntervals) <= 0.22;

  let noteDetail = division.grid === "1/32" ? 90 : division.grid === "1/16" ? 82 : division.grid === "1/8" ? 68 : 48;
  if (repeatedOnsetIntent && division.beats <= 0.5) noteDetail = Math.max(noteDetail, 80);
  if (holdRatio >= 0.5 && division.beats >= 0.5) noteDetail = 38;

  const densityLabel = division.grid === "1/32" ? "かなり細かい" : division.grid === "1/16" ? "細かい" : division.grid === "1/8" ? "ほどよい" : "ゆったり";
  return {
    bpm,
    gestures,
    onsetCount: gestures.length,
    shortestSignificantBeats: division.beats,
    shortestSignificantMs: division.beats * beatMs,
    averageNoteLengthBeats: intendedLengths.length ? intendedLengths.reduce((sum, value) => sum + value, 0) / intendedLengths.length : division.beats,
    maximumHoldBeats: holds.length ? Math.max(...holds.map((gesture) => gesture.durationBeats)) : 0,
    recommendedDivision: division.grid,
    densityLabel,
    repeatedOnsetIntent,
    recommendation: {
      grid: division.grid,
      minimumNoteBeats: division.beats,
      noteDetail,
      lengthAssist: true,
    },
  };
}
